/**
 * New Year door signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearDoorSignsPack: ToilePack = {
    slug: 'new-year-door-signs',
    category: 'season',
    name: 'New Year door signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'welcome', label: 'Welcome', subject: 'a door sign lettered "WELCOME" with a burst of confetti below, a thick border'},
        {key: 'party', label: 'Party this way', subject: 'a door sign lettered "PARTY THIS WAY" in bold with an arrow and a glass motif'},
        {key: 'quiet', label: 'Quiet please', subject: 'a door sign lettered "SHH, SLEEPING IN" with a crescent moon and stars'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a vertical page with a gold border and an even margin, the sign centred, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin, the sign centred, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a vertical page with wide margins, the sign centred, print-ready.'},
    ],
    directions: ['for a housewarming instead', 'in french instead', 'for the office door'],
    directionHint: 'try: for a housewarming instead',
    seo: {
        title: 'New Year Door Signs: Welcome, Party & Quiet Please',
        metaDescription: 'Nine New Year door signs — a welcome sign, a party-this-way arrow, and a quiet-please sign — in black-and-gold, confetti, and minimal styles.',
        h1: 'New Year door signs: welcome, party this way, quiet please',
        tagline: 'Nine signs, three messages, three styles each — lettered, bordered, ready to print.',
        intro: 'Three New Year door signs — a welcome sign, a party-this-way arrow, and a quiet-please sign for a sleeping child — each in black-and-gold, confetti-party, and minimal styles. Every word prints exactly as lettered. Ask for a different message or a name added, and the same sign grammar carries it.',
        useCases: [
            "a New Year's Eve party entrance",
            "a kids'-room quiet sign after midnight",
            'a home bar or drinks station sign',
            'a community-event door marker',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each sign is a single vertical page, sized to read clearly at door height once printed.'},
            {q: 'Can I add a name or change the wording?', a: 'Yes — the direction field takes any variation, including a name or different wording.'},
            {q: 'How do I hang the sign?', a: 'The design prints as a flat vertical sign — tape, a suction hook, or a frame handles the hanging; no hardware is included.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
